(() => {
  const GALLERY_SELECTOR = '.main-product-section .product__media-container.below-mobile.carousel.swiper';
  const THUMB_CLASS = 'immut-mobile-thumbnails';
  const BOUND = new WeakMap();
  let scanQueued = false;

  function getSlides(gallery) {
    const seen = new Set();
    return Array.from(gallery.querySelectorAll('.swiper-slide[data-media-item-id]'))
      .map((slide, domIndex) => ({
        slide,
        index: Number.isFinite(Number(slide.dataset.slideIndex)) ? Number(slide.dataset.slideIndex) : domIndex,
        mediaId: slide.dataset.mediaItemId || '',
      }))
      .sort((a, b) => a.index - b.index)
      .filter(({ index, mediaId }) => {
        const key = `${index}:${mediaId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  function signature(slides) {
    return slides.map(({ index, mediaId }) => `${index}:${mediaId}`).join('|');
  }

  function makeThumb(slide, index, imageLabel) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'immut-mobile-thumbnails__button';
    button.dataset.immutThumbIndex = String(index);
    button.setAttribute('aria-label', imageLabel.replace('__index__', String(index + 1)));

    const sourceImage = slide.querySelector('img');
    if (sourceImage) {
      const image = document.createElement('img');
      image.className = 'immut-mobile-thumbnails__image';
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.src = sourceImage.currentSrc || sourceImage.src;
      if (sourceImage.srcset) image.srcset = sourceImage.srcset;
      image.sizes = '64px';
      button.appendChild(image);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'immut-mobile-thumbnails__fallback';
      fallback.setAttribute('aria-hidden', 'true');
      fallback.textContent = String(index + 1);
      button.appendChild(fallback);
    }

    return button;
  }

  function setActive(viewport, index, shouldScroll) {
    const buttons = Array.from(viewport.querySelectorAll('[data-immut-thumb-index]'));
    let activeButton = null;

    buttons.forEach((button) => {
      const active = Number(button.dataset.immutThumbIndex) === index;
      button.classList.toggle('is-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
        activeButton = button;
      } else {
        button.removeAttribute('aria-current');
      }
    });

    if (!shouldScroll || !activeButton) return;

    const buttonLeft = activeButton.offsetLeft;
    const buttonRight = buttonLeft + activeButton.offsetWidth;
    const visibleLeft = viewport.scrollLeft;
    const visibleRight = visibleLeft + viewport.clientWidth;

    if (buttonLeft < visibleLeft || buttonRight > visibleRight) {
      viewport.scrollTo({
        left: Math.max(0, buttonLeft - (viewport.clientWidth - activeButton.offsetWidth) / 2),
        behavior: 'smooth',
      });
    }
  }

  function unbind(gallery) {
    const previous = BOUND.get(gallery);
    if (!previous) return;
    if (previous.swiper && !previous.swiper.destroyed) {
      previous.swiper.off('slideChange', previous.onSlideChange);
      previous.swiper.off('realIndexChange', previous.onSlideChange);
    }
    BOUND.delete(gallery);
  }

  function bindSwiper(gallery, viewport) {
    const swiper = gallery.swiper;
    if (!swiper || swiper.destroyed) return false;

    const previous = BOUND.get(gallery);
    if (previous && previous.swiper === swiper && previous.viewport === viewport) {
      previous.sync(false);
      return true;
    }

    unbind(gallery);

    const sync = (shouldScroll = true) => {
      const realIndex = Number.isFinite(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex;
      setActive(viewport, realIndex, shouldScroll);
    };
    const onSlideChange = () => sync(true);

    swiper.on('slideChange', onSlideChange);
    swiper.on('realIndexChange', onSlideChange);
    BOUND.set(gallery, { swiper, viewport, sync, onSlideChange });
    sync(false);
    return true;
  }

  function waitForSwiper(gallery, viewport, attempt = 0) {
    if (bindSwiper(gallery, viewport)) return;
    if (attempt >= 180 || !document.documentElement.contains(gallery)) return;
    requestAnimationFrame(() => waitForSwiper(gallery, viewport, attempt + 1));
  }

  function buildGallery(gallery) {
    const slides = getSlides(gallery);
    if (slides.length < 2) return;

    const labels = gallery.closest('[data-immut-gallery-label]')?.dataset || {};
    const imageLabel = labels.immutGalleryImageLabel || '__index__';

    const currentSignature = signature(slides);
    let viewport = gallery.nextElementSibling;

    if (viewport && viewport.classList.contains(THUMB_CLASS) && viewport.dataset.immutSignature !== currentSignature) {
      unbind(gallery);
      viewport.remove();
      viewport = null;
    }

    if (!viewport || !viewport.classList.contains(THUMB_CLASS)) {
      viewport = document.createElement('div');
      viewport.className = `${THUMB_CLASS} below-mobile`;
      viewport.dataset.immutSignature = currentSignature;
      viewport.setAttribute('role', 'group');
      viewport.setAttribute('aria-label', labels.immutGalleryLabel || '');

      const track = document.createElement('div');
      track.className = 'immut-mobile-thumbnails__track';

      slides.forEach(({ slide, index }) => {
        const button = makeThumb(slide, index, imageLabel);
        button.addEventListener('click', () => {
          const swiper = gallery.swiper;
          if (!swiper || swiper.destroyed) return;

          if (swiper.params && swiper.params.loop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(index);
          } else {
            swiper.slideTo(index);
          }
        });
        track.appendChild(button);
      });

      viewport.appendChild(track);
      gallery.insertAdjacentElement('afterend', viewport);
      gallery.classList.add('immut-mobile-gallery-ready');
    }

    waitForSwiper(gallery, viewport);
  }

  function initAll() {
    document.querySelectorAll(GALLERY_SELECTOR).forEach(buildGallery);
  }

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      initAll();
    });
  }

  function observeProductSection() {
    const root = document.querySelector('.main-product-section');
    if (!root || root.dataset.immutGalleryObserved === 'true') return;
    root.dataset.immutGalleryObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) queueScan();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    initAll();
    observeProductSection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', init);
})();
